import { Suspense, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons';
import {
    BillbackCard,
    CurrencyValue,
    CustomerFilter,
    DateTabs,
    DeductionCard,
    DeductionSortFilter,
    DeductionCategoryFilter,
    DotBillbackTypeFilter,
    ExportButton,
    Icon,
    PaginatedList,
    SearchInput,
    SuspenseBoundary,
} from 'components';
import { AdjustmentType } from 'enums';
import { formatfileNameDate } from 'functions';
import { useAccountParam, useVisibility } from 'hooks';
import {
    useAccountInfo,
    useAvailableDotBillbackTypes,
    useDeductionsFilter,
    useFilteredDeductions,
    useInvoiceSummariesByDateRange,
    useRefreshFilteredDeductions,
} from 'state';
import { PaymentDetail } from 'types';
import styles from './DeductionsPage.module.scss';

export default function DeductionsPage() {
    const accountId = useAccountParam();
    const account = useAccountInfo();
    const refresh = useRefreshFilteredDeductions(accountId);

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <HeaderComponent />
                <div className={styles.content}>
                    <div className={styles.headerContainer}>
                        <div className={styles.supplierInfo}>
                            {account.accountName} - {account.accountId}
                        </div>
                        <Suspense fallback={<SummaryTotals />}><SummaryComponent /></Suspense>
                        <FiltersComponent />
                    </div>
                    <div>
                        {accountId ? (
                            <SuspenseBoundary
                                errorMessage={`Error retrieving deductions for the selected account and date range.`}
                                onReset={refresh}
                            >
                                <DeductionsComponent />
                            </SuspenseBoundary>
                        ) : <div className={styles.message}>Please select an account to view deductions
                        </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

const DeductionsComponent = () => {
    const accountId = useAccountParam();
    const paymentDetails = useFilteredDeductions(accountId);
    const { customerId, dateRange, searchTerm, deductionType, dotBillbackType, sort } = useDeductionsFilter();
    const paginationResetKey = [
        accountId,
        customerId,
        dateRange,
        searchTerm,
        deductionType,
        dotBillbackType,
        sort.text
    ].join('_');

    return !_.isEmpty(paymentDetails) ? (
        <PaginatedList
            items={paymentDetails}
            componentKey='deductions'
            resetKey={paginationResetKey}
        >
            {(item: PaymentDetail) => (
                <>
                    {item.adjustmentTypeId === AdjustmentType.CustomerDeduction &&
                        <DeductionCard
                            key={item.invoiceId + '_' + item.rowNumber}
                            payment={item}
                        />
                    }
                    {item.adjustmentTypeId === AdjustmentType.DotBillback &&
                        <BillbackCard
                            key={item.invoiceId + '_' + item.rowNumber}
                            payment={item}
                        />
                    }
                </>
            )}
        </PaginatedList>
    ) : <div className={styles.message}>No deductions found for the selected date range and filters.</div>;
};

const DisabledExportButton = () => (
    <div className={styles.exportButton}>
        <ExportButton
            data={[]}
            fileName={''}
            fieldsToExclude={[]}
        />
    </div>
);

type BillbackTypeFilterComponentProps = {
    accountId: string,
    dotBillbackType: string | undefined,
    setDotBillbackType: (value?: string) => void,
};

const BillbackTypeFilterComponent = ({
    accountId,
    dotBillbackType,
    setDotBillbackType
}: BillbackTypeFilterComponentProps) => {
    const availableDotBillbackTypes = useAvailableDotBillbackTypes(accountId);

    useEffect(() => {
        if (dotBillbackType !== undefined && !availableDotBillbackTypes.includes(dotBillbackType)) {
            setDotBillbackType(undefined);
        }
    }, [availableDotBillbackTypes, dotBillbackType, setDotBillbackType]);

    return (
        <DotBillbackTypeFilter
            dotBillbackType={dotBillbackType}
            onDotBillbackTypeChange={setDotBillbackType}
            availableTypes={availableDotBillbackTypes}
        />
    );
};

const ExportComponent = () => {
    const accountId = useAccountParam();
    const { startDate, endDate, } = useDeductionsFilter();
    const paymentDetails = useFilteredDeductions(accountId);
    const fileDate: string = formatfileNameDate(new Date());
    const exportFileName: string = `Deductions_${startDate}_to_${endDate}_as_of_${fileDate}.xlsx`

    return (
        <div className={styles.exportButton}>
            <ExportButton
                data={paymentDetails}
                fileName={exportFileName}
                fieldsToExclude={['marDetailRows']}
            />
        </div>
    );
};

const FiltersComponent = () => {
    const accountId = useAccountParam();
    const {
        customerId,
        setCustomerId,
        searchTerm,
        setSearchTerm,
        sort,
        setSort,
        deductionType,
        setDeductionType,
        dotBillbackType,
        setDotBillbackType
    } = useDeductionsFilter();
    const isDotBillbackSelected = deductionType === AdjustmentType.DotBillback;

    useEffect(() => {
        if (!isDotBillbackSelected && dotBillbackType !== undefined) {
            setDotBillbackType(undefined);
        }
    }, [dotBillbackType, isDotBillbackSelected, setDotBillbackType]);

    return (
        <div className={styles.filters}>
            <DeductionCategoryFilter
                deductionType={deductionType}
                onDeductionTypeChange={setDeductionType}
            />
            {isDotBillbackSelected &&
                <Suspense fallback={null}>
                    <BillbackTypeFilterComponent
                        accountId={accountId}
                        dotBillbackType={dotBillbackType}
                        setDotBillbackType={setDotBillbackType}
                    />
                </Suspense>}
            <CustomerFilter
                customerId={customerId}
                onCustomerIdChange={setCustomerId}
            />
            <SearchInput
                value={searchTerm ?? ''}
                onValueChange={setSearchTerm}
            />
            <DeductionSortFilter
                deductionSort={sort}
                onDeductionSortChange={setSort}
            />
            <Suspense fallback={<DisabledExportButton />}><ExportComponent /></Suspense>
        </div>
    );
};

const HeaderComponent = () => {
    const headerRef = useRef<HTMLDivElement | null>(null);
    const shouldShowCombinedHeader = useVisibility(headerRef);
    const { dateRange, setDateRange } = useDeductionsFilter();

    const headers = useMemo(() =>
        <div className={styles.pageHeader}>
            {shouldShowCombinedHeader ?
                <div className={styles.title}>
                    <span>
                        <Icon icon={faMoneyBillTransfer} />
                    </span>
                    <span>
                        Deductions
                    </span>
                </div>
                : <></>}
            <DateTabs
                dateRange={dateRange}
                setDateRange={setDateRange}
            />
        </div>,
        [dateRange, setDateRange, shouldShowCombinedHeader]
    );

    return (
        <>
            <div className={shouldShowCombinedHeader ? styles.hidden : styles.fixedHeaderAndFilter}>
                <div>
                    {headers}
                    <FiltersComponent />
                </div>
            </div>
            <div className={!shouldShowCombinedHeader ? styles.hidden : ''} ref={headerRef}>
                {headers}
            </div>
        </>
    );
};

const SummaryComponent = () => {
    const accountId = useAccountParam();
    const { dateRange } = useDeductionsFilter();
    const paymentDetails = useFilteredDeductions(accountId);
    const invoiceSummaries = useInvoiceSummariesByDateRange(accountId, dateRange);

    const props = useMemo(() => {
        const invoiced = invoiceSummaries.reduce((sum, item) => sum + item.userVoucherDecimal || 0, 0);
        const adjustments = paymentDetails.reduce((sum, item) => sum + item.adjustmentAmount || 0, 0);
        const paid = invoiced + adjustments;

        return {
            invoiced,
            adjustments,
            paid
        };
    }, [invoiceSummaries, paymentDetails]);

    return <SummaryTotals {...props} />;
};

type SummaryTotalsProps = {
    invoiced?: number,
    adjustments?: number,
    paid?: number,
};

const SummaryTotals = ({ adjustments = 0, invoiced = 0, paid = 0 }: SummaryTotalsProps) => (
    <div className={styles.totals}>
        <span className={styles.totalAmount}>
            <b>Invoiced: </b>
            <CurrencyValue value={invoiced} />
        </span>
        <span className={styles.totalAmount}>
            <b>Adjustments: </b>
            <CurrencyValue value={adjustments} />
        </span>
        <span className={styles.totalAmount}>
            <b>Paid: </b>
            <CurrencyValue value={paid} />
        </span>
    </div>
);
